import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import Modal from '@/ui-components/Modal';
import Panel, { PanelClose } from '@/ui-components/Panel';
import Button from '@/ui-components/Button';
import Typography from '@/ui-components/Typography';
import Loading from '@/ui-components/Loading';

import useBem from '@/use/useBem';
import { userSelector, loadUser } from '@/framework/user';
import * as rewardsApi from '@/api/rewards.service';
import type { Reward } from '@/api/rewards.service';

const RewardsModal = () => {
  const navigate = useNavigate();
  const { bem } = useBem('rewards');
  const loggedIn = useRecoilValue(userSelector.loggedIn);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingSlug, setClaimingSlug] = useState<string | null>(null);

  const fetchRewards = async () => {
    const data = await rewardsApi.getRewards();
    setRewards(data.rewards ?? []);
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await rewardsApi.getRewards();
        if (!cancelled) setRewards(data.rewards ?? []);
      } catch {
        if (!cancelled) setRewards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = () => {
    navigate('/');
  };

  const handleClaim = async (slug: string) => {
    setClaimingSlug(slug);
    try {
      await rewardsApi.claimReward(slug);
      await loadUser();
      await fetchRewards();
    } catch {
      // keep current list
    } finally {
      setClaimingSlug(null);
    }
  };

  if (!loggedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={handleClose}
      contentClassName={bem('')}
    >
      <Panel>
        <PanelClose onClick={handleClose} />
        <Typography variant="xl" className={bem('h')}>
          Rewards
        </Typography>

        {loading ? (
          <Loading />
        ) : rewards.length === 0 ? (
          <Typography variant="m">No rewards available.</Typography>
        ) : (
          <div className={bem('list')}>
            {rewards.map((reward) => {
              const isClaiming = claimingSlug === reward.slug;
              const canClaim = reward.completed && !reward.claimed;

              return (
                <div key={reward.slug} className={bem('row')}>
                  <div className={bem('row-content')}>
                    <Typography variant="m" className={bem('row-title')}>
                      {reward.title}
                    </Typography>
                    <Typography variant="s" className={bem('row-description')}>
                      {reward.description}
                    </Typography>
                    <Typography variant="s" className={bem('row-progress')}>
                      {reward.progress}/{reward.target}
                      {reward.payout?.sparks != null &&
                        reward.payout.sparks > 0 &&
                        ` · ${reward.payout.sparks} sparks`}
                    </Typography>
                  </div>
                  {canClaim && (
                    <Button
                      size="medium"
                      loading={isClaiming}
                      onClick={() => handleClaim(reward.slug)}
                    >
                      Claim
                    </Button>
                  )}
                  {reward.claimed && (
                    <Typography variant="s" className={bem('claimed')}>
                      Claimed
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </Modal>
  );
};

export default RewardsModal;
